import { useEffect, useMemo, useState } from 'react';
import { Plus, ShieldAlert } from 'lucide-react';
import { AutocompleteSelect } from '../../../shared/components/AutocompleteSelect';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { Textarea } from '../../../shared/components/Textarea';
import { TextInput } from '../../../shared/components/TextInput';
import { useToast } from '../../../shared/components/Toast';
import { usePermissions } from '../../../shared/hooks/usePermissions';
import { FEATURES } from '../../../shared/lib/permissions';
import { Collaborator, mensagemDeErro } from '../api';
import { LeaveItem } from '../api/afastamentos';
import {
  useAfastamentos, useCancelarAfastamento, useProrrogarAfastamento, useRegistrarAfastamento, useRetornoAntecipado,
  useTiposAfastamento,
} from '../hooks/afastamentos';
import { useDocumentos } from '../hooks/cadastro';
import { LEAVE_STATUS_LABELS, formatDate, isValidCid, leaveDays } from './format';
import { PessoasModal } from './PessoasModal';

type Acao = { kind: 'prorrogar' | 'retorno' | 'cancelar'; leave: LeaveItem } | null;

/** Aba Afastamentos (US-GP-008). CID e tipo médico só aparecem para quem tem acesso médico. */
export function AfastamentosTab({ person }: { person: Collaborator }) {
  const toast = useToast();
  const { can } = usePermissions();
  const bond = person.bonds.find((b) => !b.closed);
  const afastamentos = useAfastamentos(person.id);
  const [registrando, setRegistrando] = useState(false);
  const [acao, setAcao] = useState<Acao>(null);
  const podeEditar = can(FEATURES.pessoasAfastamentos, 'edit');

  const lista = afastamentos.data ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs text-gray-500">
          O início e o retorno mudam a situação do vínculo automaticamente, conforme o tipo.
        </p>
        {can(FEATURES.pessoasAfastamentos, 'create') && bond && (
          <Button size="sm" onClick={() => setRegistrando(true)}><Plus className="mr-1 h-4 w-4" /> Registrar afastamento</Button>
        )}
      </div>

      {afastamentos.isLoading ? (
        <p className="text-sm text-gray-500">Carregando…</p>
      ) : lista.length === 0 ? (
        <EmptyState size="compact" title="Nenhum afastamento" />
      ) : (
        <ul className="divide-y divide-gray-100 rounded-md border border-gray-200">
          {lista.map((l) => {
            const fim = l.actualEndDate ?? l.expectedEndDate;
            return (
              <li key={l.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-3 text-sm">
                <div>
                  <p className="font-medium text-gray-900">
                    {l.type.name}
                    {l.medicalHidden && <ShieldAlert className="ml-1 inline h-3.5 w-3.5 text-gray-400" aria-label="Dado médico protegido" />}
                    <span className="ml-2 font-normal text-gray-600">{LEAVE_STATUS_LABELS[l.status]}</span>
                    {l.parentLeaveId && <span className="ml-2 text-xs text-gray-500">(prorrogação)</span>}
                  </p>
                  <p className="text-gray-700">
                    {formatDate(l.startDate)} a {formatDate(fim)} · {leaveDays(l.startDate, fim)} dias
                    {l.actualEndDate && l.actualEndDate !== l.expectedEndDate ? ` (previsto até ${formatDate(l.expectedEndDate)})` : ''}
                  </p>
                  {l.cid && <p className="text-xs text-gray-600">CID {l.cid}</p>}
                  {l.notes && <p className="text-xs text-gray-500">{l.notes}</p>}
                  {l.rejectionReason && <p className="text-xs text-red-600">Rejeitado: {l.rejectionReason}</p>}
                  {l.cancelledReason && <p className="text-xs text-gray-500">Cancelado: {l.cancelledReason}</p>}
                </div>
                {podeEditar && (
                  <div className="flex flex-wrap gap-2">
                    {(l.status === 'ativo' || l.status === 'agendado') && l.allowsExtension && !l.actualEndDate && (
                      <Button size="sm" variant="secondary" onClick={() => setAcao({ kind: 'prorrogar', leave: l })}>Prorrogar</Button>
                    )}
                    {l.status === 'ativo' && l.allowsEarlyReturn && (
                      <Button size="sm" variant="secondary" onClick={() => setAcao({ kind: 'retorno', leave: l })}>Retorno antecipado</Button>
                    )}
                    {(l.status === 'agendado' || l.status === 'aguardando_aprovacao') && (
                      <Button size="sm" variant="ghost" onClick={() => setAcao({ kind: 'cancelar', leave: l })}>Cancelar</Button>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {bond && (
        <RegistrarModal open={registrando} bondId={bond.id} personId={person.id} onClose={() => setRegistrando(false)}
          onSaved={(m) => { setRegistrando(false); toast.success(m); }} />
      )}
      <AcaoModal acao={acao} onClose={() => setAcao(null)} onDone={(m) => { setAcao(null); toast.success(m); }} />
    </div>
  );
}

function RegistrarModal({ open, bondId, personId, onClose, onSaved }: {
  open: boolean; bondId: string; personId: string; onClose: () => void; onSaved: (m: string) => void;
}) {
  const tipos = useTiposAfastamento(false, open);
  const documentos = useDocumentos(personId, false, open);
  const registrar = useRegistrarAfastamento(bondId);
  const [form, setForm] = useState({ leaveTypeId: '', startDate: '', expectedEndDate: '', cid: '', notes: '', documentId: '' });
  const [error, setError] = useState<string | null>(null);
  const tipo = (tipos.data ?? []).find((t) => t.id === form.leaveTypeId);
  const docs = useMemo(
    () => (documentos.data ?? []).filter((d) => d.category === 'afastamento' || d.category === 'ocupacional'),
    [documentos.data],
  );

  useEffect(() => {
    if (!open) return;
    setForm({ leaveTypeId: '', startDate: '', expectedEndDate: '', cid: '', notes: '', documentId: '' });
    setError(null);
  }, [open]);

  const set = (k: keyof typeof form, v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function submit() {
    if (!tipo) return setError('Selecione o tipo de afastamento.');
    if (!form.startDate || !form.expectedEndDate) return setError('Informe início e fim previsto.');
    if (form.expectedEndDate < form.startDate) return setError('O fim previsto não pode ser anterior ao início.');
    if (tipo.requiresCid && !form.cid) return setError('Este tipo exige o CID.');
    if (form.cid && !isValidCid(form.cid)) return setError('CID inválido. Use o formato da CID-10, como J06 ou J06.9.');
    if (tipo.requiresDocument && !form.documentId) return setError('Este tipo exige documento comprobatório.');
    if (tipo.notesRequired && !form.notes.trim()) return setError('Este tipo exige observação.');
    try {
      const l = await registrar.mutateAsync({
        leaveTypeId: form.leaveTypeId, startDate: form.startDate, expectedEndDate: form.expectedEndDate,
        cid: form.cid || undefined, notes: form.notes.trim() || undefined, documentId: form.documentId || undefined,
      });
      onSaved(l.status === 'aguardando_aprovacao' ? 'Afastamento enviado para aprovação.'
        : l.status === 'ativo' ? 'Afastamento registrado e iniciado.' : 'Afastamento agendado.');
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível registrar o afastamento.'));
    }
  }

  return (
    <PessoasModal open={open} size="lg" title="Registrar afastamento" submitLabel="Registrar" isSubmitting={registrar.isPending}
      submitError={error} onClose={onClose} onSubmit={submit}>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <AutocompleteSelect label="Tipo" options={(tipos.data ?? []).map((t) => ({ value: t.id, label: t.name }))}
          value={form.leaveTypeId} onChange={(v) => set('leaveTypeId', v)} />
        <div />
        <TextInput label="Início" type="date" value={form.startDate} onChange={(e) => set('startDate', e.target.value)} />
        <TextInput label="Fim previsto" type="date" value={form.expectedEndDate} onChange={(e) => set('expectedEndDate', e.target.value)} />
        {tipo?.requiresCid && <TextInput label="CID" value={form.cid} onChange={(e) => set('cid', e.target.value.toUpperCase())} />}
        {(tipo?.requiresDocument || docs.length > 0) && (
          <AutocompleteSelect
            label={tipo?.requiresDocument ? 'Documento comprobatório' : 'Documento (opcional)'}
            options={docs.map((d) => ({ value: d.id, label: `${d.typeName} · ${formatDate(d.uploadedAt)}` }))}
            value={form.documentId}
            onChange={(v) => set('documentId', v)}
          />
        )}
      </div>
      {tipo?.requiresDocument && docs.length === 0 && (
        <p className="text-xs text-amber-700">Envie o atestado na aba Documentos antes de registrar.</p>
      )}
      {tipo?.requiresApproval && <p className="text-xs text-gray-500">Este tipo exige aprovação de outro responsável.</p>}
      {tipo?.blocksAccess && <p className="text-xs text-red-700">Durante o afastamento o acesso do colaborador fica bloqueado.</p>}
      <Textarea label={tipo?.notesRequired ? 'Observação' : 'Observação (opcional)'} value={form.notes} onChange={(e) => set('notes', e.target.value)} />
    </PessoasModal>
  );
}

function AcaoModal({ acao, onClose, onDone }: { acao: Acao; onClose: () => void; onDone: (m: string) => void }) {
  const prorrogar = useProrrogarAfastamento();
  const retorno = useRetornoAntecipado();
  const cancelar = useCancelarAfastamento();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setValue('');
    setError(null);
  }, [acao]);

  async function submit() {
    if (!acao) return;
    if (!value.trim()) return setError(acao.kind === 'cancelar' ? 'Justificativa é obrigatória.' : 'Informe a data.');
    try {
      if (acao.kind === 'prorrogar') {
        await prorrogar.mutateAsync({ id: acao.leave.id, expectedEndDate: value });
        onDone('Afastamento prorrogado.');
      } else if (acao.kind === 'retorno') {
        await retorno.mutateAsync({ id: acao.leave.id, endDate: value });
        onDone('Retorno antecipado registrado.');
      } else {
        await cancelar.mutateAsync({ id: acao.leave.id, justification: value.trim() });
        onDone('Afastamento cancelado.');
      }
    } catch (err) {
      setError(mensagemDeErro(err, 'Não foi possível concluir a operação.'));
    }
  }

  const titulo = acao?.kind === 'prorrogar' ? 'Prorrogar afastamento' : acao?.kind === 'retorno' ? 'Retorno antecipado' : 'Cancelar afastamento';
  return (
    <PessoasModal
      open={acao !== null}
      title={titulo}
      description={acao?.kind === 'prorrogar'
        ? `Fim previsto atual: ${formatDate(acao.leave.expectedEndDate)}. A prorrogação começa no dia seguinte.`
        : acao?.kind === 'retorno' ? 'Informe o último dia de afastamento; o retorno é no dia seguinte.' : undefined}
      submitLabel="Confirmar"
      submitVariant={acao?.kind === 'cancelar' ? 'danger' : 'primary'}
      isSubmitting={prorrogar.isPending || retorno.isPending || cancelar.isPending}
      submitError={error}
      onClose={onClose}
      onSubmit={submit}
    >
      {acao?.kind === 'cancelar'
        ? <Textarea label="Justificativa" value={value} onChange={(e) => setValue(e.target.value)} />
        : <TextInput label={acao?.kind === 'prorrogar' ? 'Novo fim previsto' : 'Último dia de afastamento'} type="date" value={value}
          onChange={(e) => setValue(e.target.value)} />}
    </PessoasModal>
  );
}
